import { useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Form, { Field as BaseField } from "rc-field-form";
import Field from "../../../../../components/Field";
import Button from "../../../../../components/Button";
import ManageAbilitySelect from "../../../../../components/Inputs/ManageAbilitySelect";
import Checkbox from "../../../../../components/Inputs/Checkbox";
import ManageAbilityModal from "./ManageAbilityModal";
import { generateErrorsConfigForForm } from "../../../../../utils/helpers/errors";
import {
	canManageLevels,
	canManageLocations,
	hasManagingRole,
} from "../../../../../utils/helpers/user";
import {
	getSettingsOrgResourceSearchFilters,
	getSettingsResourceSearchFilters,
	renderSettingsResourceLabel,
} from "../../../../../utils/helpers/settings";
import { useProfileApi } from "../../../../../utils/api/profile";
import { usePeopleApi } from "../../../../../utils/api/people";
import { ProfileContext } from "../../../context";
import { useUser } from "../../../../../utils/hooks/user";
import { useOrgSettingsResourceLabel } from "../../../../../utils/hooks/useOrgSettingsResourceLabel";

const getUserName = (user) =>
	`${user.firstName} ${user.middleName ? `${user.middleName} ` : ""}${user.lastName
	}`;

export const getUserSearchFilters = (search) => ({
	page: 1,
	perPage: 10,
	search
});

function ChangeManagement({ close }) {
	const { t } = useTranslation();

	const [form] = Form.useForm();
	const loggedUser = useUser();
	const renderOrgLevelResourceLabel = useOrgSettingsResourceLabel();

	const { user, hierarchyGroup, submitting, submittingError, submit } =
		useContext(ProfileContext);

	const { changeManagement: profileSubmit } = useProfileApi();
	const { changeManagement: peopleSubmit } = usePeopleApi();

	const onAbilityChange = useCallback(
		(ability) => {
			switch (ability) {
				case null:
				case "manage-users":
				case "direct-company":
				case "direct-project":
				case "direct-users":
				case "no-management":
				case "direct-users-and-organisation-level":
				case "direct-users-and-location":
				case "direct-users-and-organisation-level-and-location":
				case "direct-users-and-organisation-level-and-project":
				case "direct-users-and-pay-groups":
				case "direct-users-and-calculation-group":
				case "direct-org-level-and-users":
				case "direct-by-group":
				case "direct-by-user-group": {
					form.setFieldsValue({
						managedLevels: [],
						managedLocations: [],
						managedPayGroups: [],
						managedCalcGroups: [],
						managedProjects: [],
						managedUsers: [],
						managedGroupings: [],
						managedUserGroups: [],
						additionalLevels: [],
					});
					break;
				}
				default:
					break;
			}
		},
		[form],
	);

	const onFinish = useCallback(
		(values) => {
			const data = {
				...values,
				managedLevels: values.managedLevels?.map(({ id }) => id) || [],
				managedLocations:
					values.managedLocations?.map(({ id }) => id) || [],
				managedPayGroups:
					values.managedPayGroups?.map(({ id }) => id) || [],
				managedCalcGroups:
					values.managedCalcGroups?.map(({ id }) => id) || [],
				managedProjects:
					values.managedProjects?.map(({ id }) => id) || [],
				managedUsers: values?.managedUsers?.map(({ id }) => id) || [],
				managedGroupings: values?.managedGroupings?.map(({ id }) => id) || [],
				managedUserGroups: values?.managedUserGroups?.map(({ id }) => id) || [],
				additionalLevels:
					values.manageAbility === "no-management"
						? []
						: values.additionalLevels?.map(({ id }) => id) || [],
			};

			submit(profileSubmit, peopleSubmit, data, close);
		},
		[submit, profileSubmit, peopleSubmit, close],
	);

	useEffect(() => {
		form.setFieldsValue({
			manageAbility: user?.manageAbility,
			managedLevels: user?.managedLevels,
			additionalLevels: user?.additionalLevels || [],
			managedLocations: user?.managedLocations,
			managedPayGroups: user?.managedPayGroups,
			managedCalcGroups: user?.managedCalcGroups,
			managedProjects: user?.managedProjects,
			managedUsers: user?.managedUsers,
			managedGroupings: user?.managedGroupings,
			managedUserGroups: user?.managedUserGroups,
			includeDirectUsers:
				typeof user?.includeDirectUsers !== "undefined"
					? user.includeDirectUsers
					: true,
		});
	}, [user, form]);

	useEffect(() => {
		const fieldErrors = generateErrorsConfigForForm(
			[
				"manageAbility",
				"managedLevels",
				"managedLocations",
				"managedPayGroups",
				"managedCalcGroups",
				"managedProjects",
				"managedUsers",
				"managedGroupings",
				"managedUserGroups"
			],
			submittingError,
		);
		form.setFields(fieldErrors);
	}, [form, submittingError]);

	if (!hasManagingRole(loggedUser?.roles || [])) {
		return null;
	}

	return (
		<Form
			form={form}
			layout="vertical"
			onFinish={onFinish}
			scrollToFirstError
		>
			<Field
				name="manageAbility"
				label={t("manage-ability")}
			>
				<ManageAbilitySelect
					placeholder={t("manage-ability")}
					onChange={onAbilityChange}
					allowClear
					showSearch
					hierarchyGroup={hierarchyGroup}
				/>
			</Field>

			<BaseField shouldUpdate noStyle>
				{({ }, { }, { getFieldValue }) => {
					const manageAbility = getFieldValue("manageAbility");
					const showAdditionalLevelsPicker =
						hierarchyGroup &&
						manageAbility !== "no-management";
					const manageLevels = canManageLevels(manageAbility);
					const manageLocations = canManageLocations(
						manageAbility,
						hierarchyGroup,
					);

					const managePayGroups =
						manageAbility === "direct-users-and-pay-groups";
					const manageCalcGroups =
						manageAbility === "direct-users-and-calculation-group";

					const manageProjects = ["direct-users-and-organisation-level-and-project", 'direct-project'].includes(manageAbility);

					const manageUsers = manageAbility === "manage-users";

					const manageGroups = manageAbility === "direct-by-group"

					const manageUserGroups = manageAbility === "direct-by-user-group"

					const showIncludeDirectUsersBlock =
						manageAbility &&
						manageAbility !== "direct-users" &&
						manageAbility !== "no-management";

					return (
						<>
							{showIncludeDirectUsersBlock && (
								<>
									<Field
										name="includeDirectUsers"
										valuePropName="checked"
									>
										<Checkbox id="include-direct-user">
											{t("include-direct-users")}
										</Checkbox>
									</Field>

									<ManageAbilityModal
										baseForm={form}
										name="managedUsers"
										labelPropName="description"
										resourcePath="/users"
										renderLabel={getUserName}
										mode="multiple"
										placeholder={t("users")}
										hasSearch
										getSearchFilters={getUserSearchFilters}
										label={t("manage-users")}
										rules={[
											{
												required: manageUsers,
												message: t("required-users"),
											},
										]}
										defaultValues={user?.managedUsers || []}
									/>

									<BaseField name="managedUsers" />
								</>
							)}

							{manageLevels && (
								<>
									<ManageAbilityModal
										baseForm={form}
										labelPropName="description"
										resourcePath={`/${hierarchyGroup.id}/org-levels`}
										renderLabel={renderOrgLevelResourceLabel}
										mode="multiple"
										placeholder={t("organisation-levels")}
										hasSearch
										getSearchFilters={getSettingsOrgResourceSearchFilters}
										name="managedLevels"
										label={t("manage-levels")}
										rules={[
											{
												required: true,
												message: t("required-manage-levels"),
											},
										]}
										defaultValues={user?.managedLevels || []}
									/>
									<BaseField name="managedLevels" />
								</>
							)}

							{manageLocations && (
								<>
									<ManageAbilityModal
										baseForm={form}
										labelPropName="description"
										resourcePath="/locations"
										renderLabel={renderSettingsResourceLabel}
										mode="multiple"
										placeholder={t("locations")}
										hasSearch
										getSearchFilters={getSettingsResourceSearchFilters}
										name="managedLocations"
										label={t("manage-locations")}
										rules={[
											{
												required: true,
												message: t("required-manage-locations"),
											},
										]}
										defaultValues={user?.managedLocations || []}
									/>
									<BaseField name="managedLocations" />
								</>
							)}

							{managePayGroups && (
								<>
									<ManageAbilityModal
										baseForm={form}
										mode="multiple"
										labelPropName="description"
										resourcePath="/pay-group"
										placeholder={t("pay-groups")}
										renderLabel={renderSettingsResourceLabel}
										hasSearch
										getSearchFilters={getSettingsResourceSearchFilters}
										name="managedPayGroups"
										label={t("manage-pay-groups")}
										rules={[
											{
												required: true,
												message: t("required-manage-pay-group"),
											},
										]}
										defaultValues={user?.managedPayGroups || []}
									/>
									<BaseField name="managedPayGroups" />
								</>
							)}

							{manageCalcGroups && (
								<>
									<ManageAbilityModal
										baseForm={form}
										mode="multiple"
										labelPropName="description"
										resourcePath="/calculation-group"
										placeholder={t("calculation-group")}
										renderLabel={
											renderSettingsResourceLabel
										}
										hasSearch
										getSearchFilters={getSettingsResourceSearchFilters}
										name="managedCalcGroups"
										label={t("manage-calculation-group")}
										rules={[
											{
												required: true,
												message: t("required-manage-calculation-group"),
											},
										]}
										defaultValues={user?.managedCalcGroups || []}
									/>
									<BaseField name="managedCalcGroups" />
								</>
							)}

							{manageProjects && (
								<>
									<ManageAbilityModal
										baseForm={form}
										labelPropName="description"
										resourcePath="project"
										renderLabel={renderSettingsResourceLabel}
										mode="multiple"
										placeholder={t("projects")}
										hasSearch
										getSearchFilters={getSettingsResourceSearchFilters}
										name="managedProjects"
										label={t("manage-projects")}
										rules={[
											{
												required: true,
												message: t("required-manage-projects"),
											},
										]}
										defaultValues={user?.managedProjects || []}
									/>
									<BaseField name="managedProjects" />
								</>
							)}

							{manageGroups && (
								<>
									<ManageAbilityModal
										baseForm={form}
										labelPropName="description"
										resourcePath="grouping"
										renderLabel={renderSettingsResourceLabel}
										mode="multiple"
										placeholder={t("groups")}
										hasSearch
										getSearchFilters={getSettingsResourceSearchFilters}
										name="managedGroupings"
										label={t("manage-groups")}
										rules={[
											{
												required: true,
												message: t(
													"required-manage-groups"),
											},
										]}
										defaultValues={user?.managedGroupings || []}
									/>
									<BaseField name="managedGroupings" />
								</>
							)}

							{manageUserGroups && (
								<>
									<ManageAbilityModal
										baseForm={form}
										labelPropName="description"
										resourcePath="user-group"
										renderLabel={renderSettingsResourceLabel}
										mode="multiple"
										placeholder={t("user-groups")}
										hasSearch
										getSearchFilters={getSettingsResourceSearchFilters}
										name="managedUserGroups"
										label={t("manage-user-groups")}
										rules={[
											{
												required: true,
												message: t("required-manage-user-groups"),
											},
										]}
										defaultValues={user?.managedUserGroups || []}
									/>
									<BaseField name="managedUserGroups" />
								</>
							)}
							{showAdditionalLevelsPicker && (
								<>
									<ManageAbilityModal
										baseForm={form}
										labelPropName="description"
										resourcePath={`/${hierarchyGroup.id}/org-levels`}
										renderLabel={renderOrgLevelResourceLabel}
										mode="multiple"
										placeholder={t("organisation-levels")}
										hasSearch
										getSearchFilters={
											getSettingsOrgResourceSearchFilters
										}
										name="additionalLevels"
										label={t("additional-levels")}
										defaultValues={
											user?.additionalLevels || []
										}
									/>
									<BaseField name="additionalLevels" />
								</>
							)}
								
						</>
					);
				}}
			</BaseField>

			<div className="d-flex justify-content-end">
				<Button
					className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
					onClick={close}
					disabled={submitting}
				>
					{t("cancel")}
				</Button>

				<Button
					color="primary"
					type="submit"
					loading={submitting}
					className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
				>
					{t("save")}
				</Button>
			</div>
		</Form>
	);
}

export default ChangeManagement;
